import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * ShareFlowRow — Sprint 8 follow-up (PR4).
 *
 * Single row in the Share tab's "With me" / "Out from me" lists.
 * Renders consistently across three source types — link-shares,
 * crew custody handoffs, and outgoing borrow asks — with a
 * source-tagged chip on the right.
 *
 * The row is intentionally lightweight: no avatar (we don't have
 * a counterparty photo in most cases), no thumbnail (Share tab is
 * about *flow* not items per se — Browse is the visual surface).
 * Tap → ItemDetail when caller wires an onPress; otherwise no-op.
 *
 * The source chip's color carries the most signal:
 *   - via link  → Fidelity primary tint (the brand action)
 *   - via crew  → neutral outline (it's "internal" — already part
 *                 of the user's persistent relationships)
 *   - borrow ask → tertiary container (yellow/amber-leaning — "in flight")
 */

export type ShareFlowSource = 'link' | 'crew' | 'borrow-ask';

interface ShareFlowRowProps {
  source: ShareFlowSource;
  itemName: string;
  /** Counterparty + extra context. "from Kanishk", "with Dylan", "awaiting Kenji" etc. */
  contextLabel: string;
  /** Compact time/due string — "1h", "just now", "due Mon", "5d". */
  timeLabel?: string;
  /** Visual treatment override for the time label. */
  dueState?: 'fine' | 'soon' | 'overdue';
  onPress?: () => void;
}

export const ShareFlowRow: React.FC<ShareFlowRowProps> = ({
  source,
  itemName,
  contextLabel,
  timeLabel,
  dueState = 'fine',
  onPress,
}) => {
  const chip = chipStyle(source);
  const timeColor =
    dueState === 'overdue'
      ? fpColors.error
      : dueState === 'soon'
        ? fpColors.tertiary
        : fpColors.onSurfaceVariant;

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      style={({ pressed }) => [
        styles.row,
        pressed && onPress && { backgroundColor: fpColors.surfaceContainerLow },
      ]}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={`${itemName}, ${contextLabel}`}
    >
      <View style={styles.body}>
        <Text style={styles.name} numberOfLines={1}>
          {itemName}
        </Text>
        <Text
          style={[styles.context, { color: timeColor }]}
          numberOfLines={1}
        >
          {contextLabel}
          {timeLabel ? ` · ${timeLabel}` : ''}
        </Text>
      </View>
      <View
        style={[styles.chip, { backgroundColor: chip.bg, borderColor: chip.fg }]}
        accessibilityLabel={`Source: ${chip.label}`}
      >
        <MaterialIcons name={chip.icon} size={11} color={chip.fg} />
        <Text style={[styles.chipText, { color: chip.fg }]}>{chip.label}</Text>
      </View>
    </Pressable>
  );
};

function chipStyle(source: ShareFlowSource): {
  label: string;
  bg: string;
  fg: string;
  icon: keyof typeof MaterialIcons.glyphMap;
} {
  switch (source) {
    case 'link':
      return {
        label: 'via link',
        bg: fpColors.primaryContainer,
        fg: fpColors.primary,
        icon: 'link',
      };
    case 'crew':
      return {
        // FE-201 — user-facing label flipped to "via group" while
        // the internal source enum stays 'crew' (avoid breaking
        // consumers; rename internally as separate cleanup).
        label: 'via circle',
        bg: fpColors.surfaceContainerLow,
        fg: fpColors.onSurfaceVariant,
        icon: 'groups',
      };
    case 'borrow-ask':
      return {
        label: 'borrow ask',
        bg: fpColors.tertiaryContainer,
        fg: fpColors.tertiary,
        icon: 'pan-tool',
      };
  }
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
  },
  body: { flex: 1, gap: 2 },
  name: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  context: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
  },
  chipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 10,
    letterSpacing: 0.2,
  },
});

export default ShareFlowRow;
